import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { jsPDF } from 'jspdf';
import { distinctUntilChanged, from, take } from 'rxjs';
import { STATUS } from 'src/app/components/admin/access-point/access-point.constants';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { CONSTANTS } from 'src/app/components/shared/modal/components/loading-modal/loading-modal.constants';
import { ExitModalComponent } from 'src/app/components/shared/modal/exit-modal/exit-modal.component';
import { WarningModalComponent } from 'src/app/components/shared/modal/warning-modal/warning-modal.component';
import { EUROPEAN_LOCATION_CODES } from 'src/app/components/shared/shared-constants';

import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DoNotProcessComponent } from 'src/app/components/shared/modal/do-not-process/do-not-process.component';
import { REGEX_PATTERNS, SpecialCharacters } from '../../enums';
import { LATITUDE_LONGITUDE } from '../../enums/lat-long.enum';
import { AdlDetail, UapLocation } from '../../models/admin/adl.model';
import {
  EnterpriseErrorMessage,
  InterceptError,
} from '../../models/common/api.common.model';
import { Items } from '../../models/common/items.model';
import { TableColumn } from '../../models/common/table-column.model';
import { FileUploadResponse } from '../../models/operations/operations-file-upload.model';
import { FormatType, OrientationType } from '../../models/reports/pdf.model';

@Injectable({
  providedIn: 'root',
})
export class CommonUtils {
  private lockLoadingModalUpdate = false;
  showLoadingModal = signal(false);
  loadingModalText = signal('');
  showBanner = signal(false);
  bannerType = signal(BANNER_TYPE.DOWNLOAD_SUCCESSFUL);
  errorMessage: string[] | string | null = [''];
  fileUploadResponse!: FileUploadResponse | null;
  errorHref!: string;

  unknownBannerText = signal('');
  private dialog = inject(MatDialog);

  /**
   * Flipping this will display the Loading Overlay (app-loading-modal) in app-component
   *
   * @param {boolean} newValue
   * @memberof CommonUtils
   */
  updateLoadingModal(newValue: boolean, text: string = CONSTANTS.LOADING) {
    if (!this.lockLoadingModalUpdate) {
      this.showLoadingModal.set(newValue);
      this.loadingModalText.set(text);
    }
  }

  /**
   * Flipping this will display the Loading Overlay (app-loading-modal) in app-component
   *
   * @param {boolean} newValue
   * @memberof CommonUtils
   */
  updateBannerType400Error(message: string) {
    if (!this.lockLoadingModalUpdate) {
      this.showLoadingModal.set(true);
      this.updateBannerType(BANNER_TYPE.API_400_ERROR);
      this.unknownBannerText.set(message);
    }
  }

  /**
   * Locking the loading modal
   *
   * @param {boolean} disableUpdateLoadingModal
   * @memberof CommonUtils
   */
  lockUpdateLoadingModal(disableUpdateLoadingModal: boolean) {
    this.lockLoadingModalUpdate = disableUpdateLoadingModal;
  }

  /**
   * Flipping this will display the banner
   *
   * @param {boolean} newValue
   * @memberof CommonUtils
   */
  updateBanner(newValue: boolean) {
    this.showBanner.set(newValue);
  }

  /**
   * Setting this will change what banner is displayed and then displays it
   *
   * @param {BANNER_TYPE} newValue
   * @memberof CommonUtils
   */
  updateBannerType(
    newValue: BANNER_TYPE,
    errorMessage?: string[] | string | null,
    fileUploadResponse?: FileUploadResponse,
    href?: string,
  ) {
    this.showBanner.set(false);
    setTimeout(() => {
      this.bannerType.set(newValue);
      this.showBanner.set(true);
      this.errorMessage = errorMessage ?? null;
      this.fileUploadResponse = fileUploadResponse ?? null;
      this.errorHref = href ?? '';
    }, 500);
  }

  /**
   * Prevent specific characters from being added to an input text field
   * based on SpecialCharacters enum passed in
   * @param {*} event
   * @param {SpecialCharacters} [regexType=SpecialCharacters.ALPHABET_NUMBER]
   * @return {*}  {boolean}
   * @memberof CommonUtils
   */
  preventSpecialCharacters(
    event: KeyboardEvent,
    regexType: SpecialCharacters = SpecialCharacters.ALPHABET_NUMBER,
  ): boolean {
    let regex = /[\s\S]*/;
    switch (regexType) {
      case SpecialCharacters.NO_SPACE:
        regex = /[^\s]/;
        break;
      case SpecialCharacters.DOT:
        regex = /[a-zA-Z0-9.-]/;
        break;
      case SpecialCharacters.DOT_SPACE_ALLOW:
        regex = /[a-zA-Z 0-9.-]/;
        break;
      case SpecialCharacters.ALPHABET_ONLY:
        regex = /[a-zA-Z]/;
        break;
      case SpecialCharacters.ALPHA_SPACE_ONLY:
        regex = /[a-zA-Z ]/;
        break;
      case SpecialCharacters.ALPHA_SPACE_DASH:
        regex = /[a-zA-Z -]/;
        break;
      case SpecialCharacters.ALPHABET_NUMBER:
        regex = /[a-zA-Z0-9 ]/;
        break;
      case SpecialCharacters.ALPHABET_NUMBER_NO_SPACE:
        regex = /[a-zA-Z0-9]/;
        break;
      case SpecialCharacters.NUMBER_ONLY:
        regex = /\d/;
        break;
      case SpecialCharacters.SOME:
        regex = /[\+\-\@\w]/;
        break;
      case SpecialCharacters.LAT_LONG:
        regex = /[0-9.-]/;
        break;
      case SpecialCharacters.ADDRESS:
        regex = /[a-zA-Z0-9.,\- ]/;
        break;
      case SpecialCharacters.SPECIAL_WITH_PERIOD:
        regex = /[0-9.]/;
        break;
      case SpecialCharacters.COMMA:
        regex = /[a-zA-Z0-9,]/;
        break;
      case SpecialCharacters.EMAIL:
        regex = /[a-zA-Z0-9_+\-@.]/;
        break;
      case SpecialCharacters.CURRENCY_USD:
        regex = /[$0-9.]/;
        break;
      case SpecialCharacters.TIME:
        regex = /[\d:]/;
        break;
      case SpecialCharacters.LINKED_SITES:
        regex = /[a-zA-Z\d ,/]/;
        break;
      case SpecialCharacters.DECIMAL_NUMBER:
        regex = /^\d+\.\d+$/;
        break;
      case SpecialCharacters.PICKUP_POPUP:
        regex = /[A-Za-z0-9\,\#\:\&\/\-\_\.\"\'\ ]/;
        break;
      case SpecialCharacters.PICKUP_ADDRESS:
        regex = /[A-Za-z0-9\-. ]/;
        break;
      case SpecialCharacters.ALPHABET_BASIC_PUNCT:
        regex = /[A-Za-z0-9 ,\.']/;
        break;
    }
    if (regex.test(event.key)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  /**
   * Convert passed in columns and data source to a string to feed into
   * mat table filter function
   *
   * @param {TableColumn[]} columns
   * @param {MatTableDataSource<any>} dataSource
   * @param {TableColumn[]} [selectColumns]
   * @memberof CommonUtils
   */
  searchFilter<T>(
    columns: TableColumn[],
    dataSource: MatTableDataSource<T>,
    selectColumns?: TableColumn[],
  ) {
    let newData = {};
    let newSelectData = {};
    columns.forEach((data) => {
      newData = { ...newData, [data.itemId]: data.formControl.value };
    });
    if (selectColumns) {
      selectColumns.forEach((data) => {
        newSelectData = {
          ...newSelectData,
          [data.itemId]: data.ids.length > 1 ? data.ids : data.ids[0],
        };
      });
      newData = { ...newData, ...newSelectData };
    }
    dataSource.filter = JSON.stringify(newData);
  }

  /**
   * Common function to export table as a CSV file
   *
   * @param {string[]} columnHeading
   * @param {string} fileName
   * @param {any[]} data
   * @param {string[]} [excludedFields]
   * @memberof CommonUtils
   */
  exportExcel<T>(
    columnHeading: string[],
    fileName: string,
    data: T[],
    excludedFields: string[] = [
      'actions',
      'action',
      'ACTIONS',
      'version',
      'defaultlanguage',
      'usertype',
      'pickupsassigned',
      'displayloop',
      'index',
      'availability',
    ],
  ): void {
    // init stuff
    const dataToExport: string[][] = [];
    const filteredColumnHeading = columnHeading.filter(
      (value) => !excludedFields.includes(value.toLowerCase()),
    );

    // push headers as first row
    if (filteredColumnHeading.length > 0) {
      dataToExport.push(filteredColumnHeading);
    }

    // write each value to an array, push to array of arrays for worksheet
    data.forEach((row) => {
      const exportedRow: string[] = [];
      for (const key in row) {
        if (!excludedFields.includes(key.toLowerCase())) {
          exportedRow.push((row as keyof object)[key]);
        }
      }
      dataToExport.push(exportedRow);
    });

    this.downloadCSV(dataToExport, fileName);
    this.updateBannerType(BANNER_TYPE.DOWNLOAD_SUCCESSFUL);
  }

  /**
   * Create column search objects
   *
   * @param {string} columnId
   * @param {Items[]} [ids=[]]
   * @param {string} [formControl=new FormControl('')]
   * @return {*}  {TableColumn}
   * @memberof CommonUtils
   */
  createTableColumn(
    columnId: string,
    ids: Items[] = [],
    formControl = new FormControl(''),
  ): TableColumn {
    return {
      itemId: columnId,
      ids: ids,
      formControl: formControl,
    };
  }

  /**
   *  Create filter function for mat table (intentionally
   * got rid of some typing to make generic)
   *
   * @return {*}  {(data: any, filter: string) => boolean}
   * @memberof CommonUtils
   */
  createFilter<T>(): (data: T, filter: string) => boolean {
    return (data: T, filter: string) => {
      const filterValues = JSON.parse(filter);
      return Object.keys(filterValues).every((filterKey: string) => {
        const value = filterValues[filterKey];
        const rowValue = (data as never)[filterKey];
        const searchData = (rowValue as string)?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();

        const isMultiSelect = searchValue.includes(',');
        return isMultiSelect
          ? [...value].find(
              (item: string) => item.toLowerCase() === searchData,
            ) !== undefined
          : searchData.includes(searchValue);
      });
    };
  }

  /**
   * Reset all filters, based on the data passed in.
   * To be used with Text Searches, and Dropdown Searches. NOT STATUS DROPDOWN!
   *
   * @param {MatTableDataSource<any>} dataSource
   * @param {*} originalData
   * @param {FormControl[]} textFields
   * @param {TableColumn[]} [selectFields]
   * @memberof CommonUtils
   */
  clearAllFilters<T>(
    dataSource: MatTableDataSource<T>,
    originalData: T[] | null,
    textFields: FormControl[],
    selectFields?: TableColumn[],
  ) {
    dataSource.filter = '';
    if (originalData !== null) {
      dataSource.data = originalData;
    }
    textFields.forEach((item) => {
      item.setValue('');
      item.markAsPristine();
      item.markAsUntouched();
    });
    if (selectFields) {
      selectFields.forEach((field) => {
        field.ids = [];
      });
    }
  }

  /**
   *
   *
   * @param {Items[]} selectedColumnList
   * @param {boolean} selectAll
   * @return {*}  {*}
   * @memberof CommonUtils
   */
  selectAllForCheckedColumn(selectedColumnList: Items[], selectAll: boolean) {
    selectedColumnList.forEach((item) => {
      item.selected = !selectAll;
    });
  }

  /**
   * Used to generate PDF from html with given parameters
   *
   * @param {*} htmlContent
   * @param {string} reportTitle
   * @param {string} fileName
   * @param {OrientationType} [orientation='p']
   * @param {FormatType} [format='letter']
   * @memberof CommonUtils
   */
  generatePdfFromHtml(
    htmlContent: string,
    reportTitle: string,
    fileName: string,
    orientation: OrientationType = 'p',
    format: FormatType = 'letter',
    recreatePDF = false,
    noPages = 0,
  ) {
    const doc = new jsPDF({ orientation, format, unit: 'mm' });
    const pageWidthMM = doc.internal.pageSize.getWidth();
    const margins: [number, number, number, number] = [4, 12, 12, 4];
    const contentWidthMM = pageWidthMM - (margins[1] + margins[3]);
    const windowWidthPx = 1024;

    const img = new Image();
    img.src = 'assets/UPS_Shield_Black.png';
    doc.addImage(img, 'png', 4, 4, 15, 15);
    doc.text(reportTitle, 20, 12);
    doc.setFont('helvetica');
    doc.setFontSize(18);
    if (recreatePDF) {
      let val = 0;
      do {
        doc.addPage(format, orientation);
        val++;
      } while (val < noPages - 1);
    }
    doc.html(htmlContent, {
      callback: () => {
        this.addPdfFooter(doc);
        //Re-call this function to solve an issue with acrobat thinking the pdf is invalid
        //https://github.com/parallax/jsPDF/issues/3428
        if (!recreatePDF) {
          this.generatePdfFromHtml(
            htmlContent,
            reportTitle,
            fileName,
            orientation,
            format,
            true,
            doc.getNumberOfPages(),
          );
        } else {
          doc.save(`${fileName}.pdf`);
          this.lockUpdateLoadingModal(false);
          this.updateLoadingModal(false);
          this.updateBannerType(BANNER_TYPE.DOWNLOAD_SUCCESSFUL);
        }
      },
      margin: [4, 12, 12, 4],
      x: 0,
      y: 20,
      width: contentWidthMM,
      windowWidth: windowWidthPx, // 816 = letter paper pixel width at 96dpi (web), 786 = less the 30px margin
      autoPaging: 'text',
      html2canvas: {
        logging: false,
        windowWidth: windowWidthPx, // 816 = letter paper pixel width at 96dpi (web), 786 = less the 30px margin
        letterRendering: true,
      },
    });
  }

  /**
   *
   * Used to add footer text in pdf printed date and page numbers
   * @param {jsPDF} doc
   * @memberof CommonUtils
   */
  addPdfFooter(doc: jsPDF) {
    const printedDate = formatDate(new Date(), 'EEE MMM dd hh:mm', 'en-US');
    const pageCount = Array(doc.getNumberOfPages()).fill(0);
    pageCount.forEach((_page, index) => {
      doc.setFont('helvetica');
      doc.setFontSize(8);
      doc.setPage(index + 1);
      const pageText = `Page ${index + 1} of ${pageCount.length}`;
      // Tue Jan 23 16:02
      const printedText = `Printed ${printedDate}`;
      const pageHeight = doc.internal.pageSize.height - 8;
      const pageWidth = doc.internal.pageSize.width - 35;
      const margin = 4;
      doc.text(printedText, margin, pageHeight);
      doc.text(pageText, pageWidth, pageHeight);
    });
  }

  /**
   *  Validate Lat Long Inputs for FormControls.
   * Doesn't allow Long > 180 or < -180
   * Doesn't allow Lat > 90 or < -90
   *
   * @param {LATITUDE_LONGITUDE} value
   * @return {*}  {ValidatorFn}
   * @memberof CommonUtils
   */
  latLongValidator(value: LATITUDE_LONGITUDE): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const regex = /(-?)(\d{2,3})(\.)(\d{8,10})/;
      const isValidLongitude =
        regex.test(control.value) &&
        control.value < 180.000000001 &&
        control.value > -180.00000001;
      const isValidLatitude =
        regex.test(control.value) &&
        control.value < 90.0000000001 &&
        control.value > -90.000000001;
      if (value === LATITUDE_LONGITUDE.LATITUDE) {
        return !isValidLatitude ? { latLongInvalid: true } : null;
      }
      if (value === LATITUDE_LONGITUDE.LONGITUDE) {
        return !isValidLongitude ? { latLongInvalid: true } : null;
      }
      return null;
    };
  }

  /**
   * Used to get formated date yyyy-mm-dd
   *
   * @param {Date} date
   * @return {*}
   * @memberof CommonUtils
   */
  getFormattedDate(date: Date) {
    if (date) {
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];
    } else {
      return '';
    }
  }

  /**
   * Used to convert a base64 string from BE into a PDF
   *
   * @param {string} base64String
   * @param {string} fileName
   * @memberof CommonUtils
   */
  convertBase64toPdf(base64String: string, fileName: string) {
    const source = `data:application/pdf;base64,${base64String}`;
    const link = document.createElement('a');
    link.href = source;
    link.download = `${fileName}.pdf`;
    link.click();
    link.remove();
  }

  openExitModal(touched: boolean) {
    if (touched) {
      this.dialog.open(ExitModalComponent, {
        width: '20rem',
        height: '14rem',
        disableClose: true,
        autoFocus: false,
      });
    } else {
      this.dialog.closeAll();
    }
  }

  openWarningModal<T>(data: T) {
    return this.dialog.open(WarningModalComponent, {
      width: 'min(24.375rem, calc(100vw - 2rem))',
      minHeight: '15.2rem',
      disableClose: true,
      autoFocus: false,
      data: data,
    });
  }

  updateDropdown(dropdownOpen: boolean[], index: number) {
    return dropdownOpen.map((value, i) => {
      if (i !== index) {
        return false;
      } else {
        return !value;
      }
    });
  }

  convertStatus(statusCode: string): string {
    switch (statusCode) {
      case '01':
        return STATUS.ACTIVE.toUpperCase();
      case '02':
        return STATUS.INACTIVE.toUpperCase();
      case '03':
        return STATUS.CANCELLED.toUpperCase();
      case '04':
        return STATUS.PENDING.toUpperCase();
      case '05':
        return STATUS.INCOMPLETE.toUpperCase();
      case '06':
        return STATUS.SUSPENDED.toUpperCase();
      case '07':
        return STATUS.ACTIVE_UNAVAILABLE.toUpperCase();
      case '08':
        return STATUS.TERMINATED.toUpperCase();
      default:
        return STATUS.UNKNOWN.toUpperCase();
    }
  }

  filterOutInvalidAccessPoints(values: AdlDetail[] | UapLocation[]) {
    return values.filter(
      (value) =>
        value.uapStatus !== null &&
        this.convertStatus(value.uapStatus) !== STATUS.TERMINATED,
    );
  }

  isStateProvinceOptional(
    countryCode?: string,
    groupCode?: string,
    locCode?: string,
  ) {
    return (
      EUROPEAN_LOCATION_CODES.includes(locCode ?? '') ||
      groupCode === 'R16' ||
      groupCode === 'R17' ||
      countryCode === 'TW' ||
      countryCode === 'SG' ||
      countryCode === 'TH'
    );
  }

  downloadCSV(data: string[][], exportFileName: string) {
    const formattedData = data
      .map((row) =>
        row
          .map((cell) => {
            if (/["\n,]/.test(cell)) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          })
          .join(','),
      )
      .join('\n');
    const blob = new Blob([formattedData], { type: 'application/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = `${exportFileName}.csv`;
    a.href = url;
    a.hidden = true;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  autoFocusOnNewInput(index: number) {
    //Auto-focus on the tracking number input when adding a new number
    setTimeout(() => {
      const inputs = document.getElementsByTagName('ion-input');
      if (inputs[inputs.length - index]) {
        from(
          document
            .getElementsByTagName('ion-input')
            [inputs.length - index]?.setFocus(),
        )
          .pipe(take(1))
          .subscribe();
      }
    }, 100);
  }

  sanitizePaste(
    event: ClipboardEvent,
    type: keyof typeof REGEX_PATTERNS = 'alphanumeric',
  ) {
    event.preventDefault();

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const pastedInput: string =
      event.clipboardData?.getData('text/plain') ?? '';

    const regex = REGEX_PATTERNS[type];
    if (!regex) {
      console.warn(
        `Regex type "${type}" not found. Falling back to alphanumeric.`,
      );
      return;
    }

    let cleaned = pastedInput
      .split('')
      .filter((char) => regex.test(char))
      .join('');

    const maxLength = target.maxLength > 0 ? target.maxLength : Infinity;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;
    const currentLength = target.value.length - (end - start);

    if (currentLength + cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength - currentLength);
    }

    target.setRangeText(cleaned.trim(), start, end, 'end');
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }

  openDoNotProcessModal(errorVals: InterceptError[]) {
    const trackingNums = errorVals
      .filter((val) => val.message === EnterpriseErrorMessage)
      .map((val) => val.trackingNumber);

    if (trackingNums.length > 0) {
      return this.dialog.open(DoNotProcessComponent, {
        width: '30rem',
        minHeight: '17rem',
        disableClose: true,
        data: {
          trackingNumberList: trackingNums,
        },
      });
    } else {
      return { afterClosed: () => {} } as MatDialogRef<
        DoNotProcessComponent,
        any
      >;
    }
  }

  async autoCapitalize(
    form: FormGroup,
    controls: string[],
    destroy: DestroyRef,
  ) {
    controls.forEach((controlName) => {
      const ctrl = form.get(controlName);
      if (!ctrl) return;

      ctrl.valueChanges
        .pipe(takeUntilDestroyed(destroy), distinctUntilChanged())
        .subscribe(async (val) => {
          if (val == null) return;

          const next = val.toString().toUpperCase();
          if (next === val) return;

          // Find the ion-input for this control
          const ionInput = document.querySelector(
            `ion-input[formControlName="${controlName}"]`,
          ) as HTMLIonInputElement;

          if (!ionInput) {
            // fallback if selector fails
            ctrl.setValue(next, { emitEvent: false });
            return;
          }

          // Get native input
          const nativeInput = await ionInput.getInputElement();

          // Save cursor position
          const start = nativeInput.selectionStart;
          const end = nativeInput.selectionEnd;

          // Update control
          ctrl.setValue(next, { emitEvent: false });

          // Restore cursor AFTER DOM re-render
          setTimeout(() => {
            try {
              nativeInput.setSelectionRange(start, end);
            } catch {}
          });
        });
    });
  }
}
